import { useState, useEffect } from "react";
import { SKU } from "@/data/mockData";
import { useProcurement } from "@/context/ProcurementContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  Package, 
  Edit, 
  TrendingUp, 
  TrendingDown,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  BarChart3,
  FileText,
  Calendar,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  Activity
} from "lucide-react";
import { StockAlert } from "./StockAlert";
import { PriceHistoryChart } from "./PriceHistoryChart";
import { VendorPricingComparison } from "./VendorPricingComparison";
import { getVendorPriceComparison } from "@/data/mockData";
import { toast } from "sonner";

interface SKUDetailsProps {
  skuId: number;
  onClose: () => void;
  onEdit: (id: number) => void;
}

export const SKUDetails = ({ skuId, onClose, onEdit }: SKUDetailsProps) => {
  const { state } = useProcurement();
  const [activeTab, setActiveTab] = useState("overview");
  const [showPriceChart, setShowPriceChart] = useState(false);

  const sku = state.skus.find(s => s.id === skuId);

  if (!sku) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>SKU Not Found</DialogTitle>
            <DialogDescription>The requested SKU could not be found.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Calculate stock metrics
  const stockPercentage = sku.maxStock > 0 ? (sku.currentStock / sku.maxStock) * 100 : 0;
  const reorderPercentage = sku.maxStock > 0 ? (sku.reorderLevel / sku.maxStock) * 100 : 0;
  const stockStatus = sku.currentStock <= 0 ? 'out-of-stock' : 
                     sku.currentStock <= sku.reorderLevel ? 'low-stock' : 
                     sku.currentStock <= sku.reorderLevel * 1.5 ? 'warning' : 'good';

  // Get stock status details
  const getStockStatusDetails = () => {
    switch (stockStatus) {
      case 'out-of-stock':
        return { label: 'Out of Stock', variant: 'destructive' as const, icon: XCircle, color: 'text-red-500' };
      case 'low-stock':
        return { label: 'Low Stock', variant: 'destructive' as const, icon: AlertTriangle, color: 'text-orange-500' };
      case 'warning':
        return { label: 'Warning', variant: 'secondary' as const, icon: AlertTriangle, color: 'text-yellow-500' };
      default:
        return { label: 'In Stock', variant: 'default' as const, icon: CheckCircle, color: 'text-green-500' };
    }
  };

  const stockStatusDetails = getStockStatusDetails();

  // Get price trend
  const getPriceTrend = () => {
    if (sku.priceHistory.length < 2) return null;
    
    const latest = sku.priceHistory[sku.priceHistory.length - 1];
    const previous = sku.priceHistory[sku.priceHistory.length - 2];
    
    if (latest.price > previous.price) {
      return { 
        trend: 'up', 
        percentage: ((latest.price - previous.price) / previous.price * 100).toFixed(1),
        color: 'text-red-500'
      };
    } else if (latest.price < previous.price) {
      return { 
        trend: 'down', 
        percentage: ((previous.price - latest.price) / previous.price * 100).toFixed(1),
        color: 'text-green-500'
      };
    }
    return { trend: 'stable', percentage: '0', color: 'text-gray-500' };
  };

  const priceTrend = getPriceTrend();

  // Get vendor details
  const getVendorDetails = (vendorId: number) => {
    return state.vendors.find(v => v.id === vendorId);
  };

  // Get recent purchase orders
  const getRecentPOs = () => {
    return state.purchaseOrders
      .filter(po => po.items.some(item => item.skuId === skuId))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  };

  // Get recent quality inspections
  const getRecentInspections = () => {
    return state.qualityInspections
      .filter(qi => qi.items.some(item => item.skuId === skuId))
      .sort((a, b) => new Date(b.inspectionDate).getTime() - new Date(a.inspectionDate).getTime())
      .slice(0, 3);
  };

  const recentPOs = getRecentPOs();
  const recentInspections = getRecentInspections();

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {sku.name}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-4 mt-2">
                <span>{sku.skuCode}</span>
                <Badge variant="outline">{sku.category}</Badge>
                <Badge variant={sku.isActive ? "default" : "secondary"}>
                  {sku.isActive ? "Active" : "Inactive"}
                </Badge>
              </DialogDescription>
            </div>
            <Button onClick={() => onEdit(sku.id)} className="ml-4">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="vendors">Vendors</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="specifications">Specifications</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Stock Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3 className="h-4 w-4" />
                    Stock Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <stockStatusDetails.icon className={`h-5 w-5 ${stockStatusDetails.color}`} />
                    <Badge variant={stockStatusDetails.variant}>
                      {stockStatusDetails.label}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Current Stock</span>
                      <span className="font-medium">{sku.currentStock.toLocaleString()} {sku.unit}</span>
                    </div>
                    <Progress value={stockPercentage} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Reorder: {sku.reorderLevel.toLocaleString()}</span>
                      <span>Max: {sku.maxStock.toLocaleString()}</span>
                    </div>
                  </div>

                  {stockStatus !== 'good' && (
                    <StockAlert sku={sku} />
                  )}
                </CardContent>
              </Card>

              {/* Price Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <DollarSign className="h-4 w-4" />
                    Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {sku.linkedVendors.length > 0 ? (
                    <>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm">Current Price</span>
                          <span className="font-medium text-lg">
                            {formatCurrency(sku.linkedVendors[0]?.defaultPrice || 0)}
                          </span>
                        </div>
                        {priceTrend && (
                          <div className="flex items-center gap-2 text-sm">
                            {priceTrend.trend === 'up' && <TrendingUp className="h-4 w-4 text-red-500" />}
                            {priceTrend.trend === 'down' && <TrendingDown className="h-4 w-4 text-green-500" />}
                            <span className={priceTrend.color}>
                              {priceTrend.trend === 'stable' ? 'No change' : `${priceTrend.percentage}% ${priceTrend.trend}`}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowPriceChart(true)}
                        className="w-full"
                      >
                        <TrendingUp className="h-4 w-4 mr-2" />
                        View Price History
                      </Button>
                    </>
                  ) : (
                    <div className="text-center text-muted-foreground text-sm">
                      No pricing information
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Vendor Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="h-4 w-4" />
                    Vendors
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Total Vendors</span>
                      <span className="font-medium">{sku.linkedVendors.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Preferred</span>
                      <span className="font-medium">
                        {sku.linkedVendors.filter(v => v.isPreferred).length}
                      </span>
                    </div>
                  </div>

                  {sku.linkedVendors.length > 0 && (
                    <div className="space-y-1">
                      {sku.linkedVendors.slice(0, 2).map((link) => {
                        const vendor = getVendorDetails(link.vendorId);
                        return (
                          <div key={link.vendorId} className="text-xs p-2 bg-muted rounded">
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{vendor?.name || 'Unknown'}</span>
                              {link.isPreferred && <Badge variant="default" className="text-xs">Preferred</Badge>}
                            </div>
                            <div className="text-muted-foreground">
                              {formatCurrency(link.defaultPrice)} | {link.leadTime}d lead time
                            </div>
                          </div>
                        );
                      })}
                      {sku.linkedVendors.length > 2 && (
                        <div className="text-xs text-muted-foreground text-center">
                          +{sku.linkedVendors.length - 2} more vendors
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Description and Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Product Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-muted-foreground">{sku.description}</p>
                </div>
                
                <Separator />
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Unit:</span>
                    <p className="font-medium">{sku.unit}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>
                    <p className="font-medium">{sku.category}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium">{new Date(sku.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Updated:</span>
                    <p className="font-medium">{new Date(sku.updatedAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors" className="space-y-4">
            {/* Vendor Pricing Comparison */}
            <VendorPricingComparison
              comparisons={getVendorPriceComparison(skuId, 100)} // Default quantity of 100
              skuName={sku.name}
              quantity={100}
            />
            
            <Card>
              <CardHeader>
                <CardTitle>Vendor Details</CardTitle>
                <CardDescription>
                  Detailed vendor information and contact details
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sku.linkedVendors.length > 0 ? (
                  <div className="space-y-4">
                    {sku.linkedVendors.map((link) => {
                      const vendor = getVendorDetails(link.vendorId);
                      return (
                        <Card key={link.vendorId}>
                          <CardContent className="pt-6">
                            <div className="flex items-start justify-between">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{vendor?.name || 'Unknown Vendor'}</h4>
                                  {link.isPreferred && (
                                    <Badge variant="default">Preferred</Badge>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground space-y-1">
                                  <div className="flex items-center gap-2">
                                    <Mail className="h-3 w-3" />
                                    {vendor?.contactDetails.email}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Phone className="h-3 w-3" />
                                    {vendor?.contactDetails.phone}
                                  </div>
                                  {vendor?.contactDetails.website && (
                                    <div className="flex items-center gap-2">
                                      <ExternalLink className="h-3 w-3" />
                                      <a href={vendor.contactDetails.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                        Website
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="text-right space-y-2">
                                <div className="text-2xl font-bold">{formatCurrency(link.defaultPrice)}</div>
                                <div className="text-sm text-muted-foreground space-y-1">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {link.leadTime} days lead time
                                  </div>
                                  <div>Min. Order: {link.minimumOrderQty.toLocaleString()} {sku.unit}</div>
                                  <div>Updated: {new Date(link.lastUpdated).toLocaleDateString()}</div>
                                </div>
                              </div>
                            </div>
                            
                            {vendor && (
                              <div className="mt-4 pt-4 border-t">
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <span className="text-muted-foreground">Performance:</span>
                                    <div className="flex items-center gap-1 mt-1">
                                      <div className="text-lg font-semibold">{vendor.performanceRating.toFixed(1)}</div>
                                      <div className="text-muted-foreground">/5.0</div>
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">On-Time Delivery:</span>
                                    <div className="text-lg font-semibold mt-1">
                                      {vendor.deliveryMetrics.totalOrders > 0 
                                        ? Math.round((vendor.deliveryMetrics.onTimeDeliveries / vendor.deliveryMetrics.totalOrders) * 100)
                                        : 0}%
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Quality Score:</span>
                                    <div className="text-lg font-semibold mt-1">{vendor.deliveryMetrics.qualityScore.toFixed(1)}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No vendors linked to this SKU</p>
                    <p className="text-sm">Edit the SKU to add vendor relationships</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Price History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Price History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sku.priceHistory.length > 0 ? (
                    <div className="space-y-3">
                      {sku.priceHistory.slice(-5).reverse().map((entry) => {
                        const vendor = getVendorDetails(entry.vendorId);
                        const user = state.users.find(u => u.id === entry.changedBy);
                        return (
                          <div key={entry.id} className="flex justify-between items-center p-3 border rounded">
                            <div>
                              <div className="font-medium">{formatCurrency(entry.price)}</div>
                              <div className="text-sm text-muted-foreground">
                                {vendor?.name} • {new Date(entry.date).toLocaleDateString()}
                              </div>
                              {entry.reason && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {entry.reason}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              by {user?.name}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      No price history available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Purchase Orders */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Recent Orders
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentPOs.length > 0 ? (
                    <div className="space-y-3">
                      {recentPOs.map((po) => {
                        const item = po.items.find(i => i.skuId === skuId);
                        const vendor = getVendorDetails(po.vendorId);
                        return (
                          <div key={po.id} className="flex justify-between items-center p-3 border rounded">
                            <div>
                              <div className="font-medium">{po.poNumber}</div>
                              <div className="text-sm text-muted-foreground">
                                {vendor?.name} • {item?.quantity.toLocaleString()} {sku.unit}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(po.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                            <Badge variant={
                              po.status === 'FULLY_RECEIVED' ? 'default' :
                              po.status === 'CREATED' ? 'secondary' :
                              'outline'
                            }>
                              {po.status}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      No recent orders found
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Specifications Tab */}
          <TabsContent value="specifications" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Technical Specifications</CardTitle>
                <CardDescription>
                  Detailed technical information and specifications for this SKU
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sku.specifications && Object.keys(sku.specifications).length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(sku.specifications).map(([key, value]) => (
                      <div key={key} className="p-4 border rounded-lg">
                        <div className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </div>
                        <div className="text-lg font-semibold mt-1">{value}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No specifications available</p>
                    <p className="text-sm">Edit the SKU to add technical specifications</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Recent Activity
                </CardTitle>
                <CardDescription>
                  Recent transactions and quality inspections for this SKU
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Recent Quality Inspections */}
                  {recentInspections.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">Quality Inspections</h4>
                      <div className="space-y-3">
                        {recentInspections.map((inspection) => {
                          const item = inspection.items.find(i => i.skuId === skuId);
                          const inspector = state.users.find(u => u.id === inspection.inspectorId);
                          return (
                            <div key={inspection.id} className="flex items-center justify-between p-3 border rounded">
                              <div>
                                <div className="font-medium">Inspection #{inspection.id}</div>
                                <div className="text-sm text-muted-foreground">
                                  Inspector: {inspector?.name} • {new Date(inspection.inspectionDate).toLocaleDateString()}
                                </div>
                                {item && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    Accepted: {item.acceptedQty}/{item.receivedQty} • Rating: {item.qualityRating}/5
                                  </div>
                                )}
                              </div>
                              <Badge variant={
                                inspection.overallStatus === 'Passed' ? 'default' :
                                inspection.overallStatus === 'Failed' ? 'destructive' :
                                'secondary'
                              }>
                                {inspection.overallStatus}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {recentPOs.length === 0 && recentInspections.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No recent activity</p>
                      <p className="text-sm">Activity will appear here as transactions occur</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Price History Chart Modal */}
        {showPriceChart && (
          <PriceHistoryChart
            skuId={skuId}
            onClose={() => setShowPriceChart(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
